import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthApi, ApiClient } from "./tradingApiSdk/src";

// CloudScape components
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { 
  AppLayout, 
  BreadcrumbGroup, 
  Button,
  ButtonDropdown,
  Cards,
  Container, 
  ContentLayout, 
  Header,
  HelpPanel,
  Link,
  Pagination,
  SideNavigation,
  SpaceBetween,
  Spinner,
  Tabs,
  Box,
  ColumnLayout
} from "@cloudscape-design/components";
import { applyMode, Mode } from '@cloudscape-design/global-styles';

// Helper function to detect API URL based on environment
function detectApiBaseUrl() {
  const hostname = window.location.hostname;
  if (hostname.includes("beta")) {
    return "https://beta-api.edge.a1ultra.com";
  } else if (hostname.includes("127.0.0.1")) {
    return "http://127.0.0.1:5000";
  } else {
    return "https://bpgsxuj2jcm33m5w3p55sbfrtm0sitcn.lambda-url.ap-south-1.on.aws";
  }
}

export default function HomePage({ onLogout }) {
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState("user-profile");
  const [toolsOpen, setToolsOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorMode] = useState('light');
  const [userData, setUserData] = useState({
    username: "",
    role: "",
    onboardingComplete: false,
    zerodhaConnected: false
  });

  // Effect to check authentication status and get user data
  useEffect(() => {
    async function checkStatusAndLoadData() {
      try { 
        const client = new ApiClient();
        client.basePath = detectApiBaseUrl();
        client.enableCookies = true;
        client.fetchOptions = { credentials: "include", cache: "no-cache" };

        // Check auth status
        const res = await fetch(`${client.basePath}/auth/status`, {
          credentials: "include",
          cache: "no-cache",
        });
        const data = await res.json();

        if (!data.logged_in) {
          onLogout();
          navigate("/login", { replace: true });
          return;
        }
        
        // Get user profile data
        try {
          const onboardingRes = await fetch(`${client.basePath}/onboarding/my_onboarding`, {
            credentials: "include",
          });
          
          if (onboardingRes.ok) {
            const onboardingData = await onboardingRes.json();
            setUserData({
              username: data.username || "User",
              role: onboardingData.role || "user",
              onboardingComplete: onboardingData.onboarding_status === "COMPLETED",
              zerodhaConnected: false, // Will be set later
              ...onboardingData
            });
          } else {
            setUserData({
              username: data.username || "User",
              role: "user",
              onboardingComplete: false,
              zerodhaConnected: false
            });
          }
          
          // Check Zerodha status
          const readyRes = await fetch(`${client.basePath}/token/is_ready`, {
            credentials: "include",
          });
          if (readyRes.ok) {
            const readyData = await readyRes.json();
            setUserData(prev => ({
              ...prev,
              zerodhaConnected: readyData.ready
            }));
          }
        } catch (profileErr) {
          console.error("Error fetching user profile:", profileErr);
          setUserData({
            username: data.username || "User",
            role: "user",
            onboardingComplete: false,
            zerodhaConnected: false
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error verifying session:", err);
        onLogout();
        navigate("/login", { replace: true });
      }
    }
    
    checkStatusAndLoadData();
  }, [navigate, onLogout]);

  // Toggle color mode between light and dark
  const toggleMode = () => {
    const newMode = colorMode === 'light' ? 'dark' : 'light';
    setColorMode(newMode);
    applyMode(newMode === 'light' ? Mode.Light : Mode.Dark);
  };

  // Handle the utility menu clicks (profile, preferences, logout)
  const handleUtilityClick = async (itemId) => {
    if (itemId === "logout") {
      try {
        const client = new ApiClient();
        client.enableCookies = true;
        client.basePath = detectApiBaseUrl();
        client.fetchOptions = { credentials: "include" };

        const authApi = new AuthApi(client);
        await authApi.getLogoutResource();

        // Clear session cookies
        document.cookie = "session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = "session=; Max-Age=0; path=/; domain=" + window.location.hostname;

        onLogout();
        navigate("/login", { replace: true });

      } catch (err) {
        console.error("Logout error", err);
      }
    } else if (itemId === "preferences") {
      toggleMode(); // Toggle between light and dark mode
    }
  };

  // Navigation items for the side navigation panel
  const navigationItems = [
    {
      type: "section",
      text: "Dashboard",
      items: [
        { type: "link", text: "Overview", href: "#/overview" },
        { type: "link", text: "Analytics", href: "#/analytics" }
      ]
    },
    {
      type: "section",
      text: "Trading",
      items: [
        { type: "link", text: "Strategies", href: "#/strategies" },
        { type: "link", text: "Orders", href: "#/orders" },
        { type: "link", text: "Positions", href: "#/positions" }
      ]
    },
    {
      type: "section",
      text: "Account",
      items: [
        { type: "link", text: "Profile", href: "#/profile" },
        { type: "link", text: "Zerodha Connection", href: "#/zerodha" }
      ]
    }
  ];

  // Utility menu items for the top navigation
  const topNavUtilities = [
    {
      type: "menu-dropdown",
      text: userData.username,
      description: userData.role === "admin" ? "Administrator" : "Trader",
      iconName: "user-profile",
      items: [
        { id: "profile", text: "Profile" },
        { id: "preferences", text: "Toggle Dark Mode" },
        { id: "logout", text: "Sign out" },
      ],
      onItemClick: (e) => {
        handleUtilityClick(e.detail.id);
      },
    },
  ];

  // Action dropdown for the header
  const headerActions = (
    <SpaceBetween direction="horizontal" size="xs">
      <ButtonDropdown
        items={[
          { id: "refresh", text: "Refresh data" },
          { id: "export", text: "Export to CSV" }
        ]}
        variant="primary"
      >
        Actions
      </ButtonDropdown>
    </SpaceBetween>
  );

  // If still loading, show a spinner
  if (loading) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner size="large" />
        <Box variant="p" padding={{ top: "s" }}>
          Loading your dashboard...
        </Box>
      </Box>
    );
  }

  // Main render
  return (
    <>
      <TopNavigation
        identity={{
          href: "#",
          title: "A1Ultra Trading Edge",
          logo: {
            src: "https://placeholdit.imgix.net/~text?txtsize=8&txt=30%C3%9730&w=30&h=30",
            alt: "A1Ultra Logo"
          }
        }}
        utilities={topNavUtilities}
        i18nStrings={{
          searchIconAriaLabel: "Search",
          searchDismissIconAriaLabel: "Close search",
          overflowMenuTriggerText: "More",
          overflowMenuTitleText: "All",
        }}
      />
      
      <AppLayout
        contentType="dashboard"
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={
          <SideNavigation
            header={{ text: "Trading Edge", href: "#" }}
            items={navigationItems}
            activeHref="#/overview"
          />
        }
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        tools={
          <HelpPanel
            header={<h2>Trading Edge Help</h2>}
            footer={
              <div>
                <h3>Learn more</h3>
                <ul>
                  <li><Link href="#/docs">Documentation</Link></li>
                  <li><Link href="#/help">Support Center</Link></li>
                  <li><Link href="#/api">API Reference</Link></li>
                </ul>
              </div>
            }
          >
            <div>
              <h3>Getting Started</h3>
              <p>
                A1Ultra Trading Edge helps you automate your trading strategies and execute them with precision.
                Complete your onboarding to start using the platform.
              </p>
              <h3>Zerodha Integration</h3>
              <p>
                Connect your Zerodha account to start placing real orders through our platform.
                Ensure you have your API key and secret ready.
              </p>
            </div>
          </HelpPanel>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "Home", href: "#" },
              { text: "Dashboard", href: "#" }
            ]}
          />
        }
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                actions={headerActions}
                description="Manage your trading experience"
              >
                Welcome to A1Ultra Trading Edge
              </Header>
            }
          >
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
              tabs={[
                {
                  id: "user-profile",
                  label: "Your Profile",
                  content: (
                    <Container
                      header={
                        <Header variant="h2">User Information</Header>
                      }
                    >
                      <ColumnLayout columns={2} variant="text-grid">
                        <SpaceBetween size="l">
                          <div>
                            <Box variant="awsui-key-label">Username</Box>
                            <div>{userData.username}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Role</Box>
                            <div>{userData.role}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Onboarding Status</Box>
                            <div>{userData.onboarding_status || "Not Started"}</div>
                          </div>
                        </SpaceBetween>
                        <SpaceBetween size="l">
                          <div>
                            <Box variant="awsui-key-label">Zerodha Connection</Box>
                            <div>{userData.zerodhaConnected ? "Connected" : "Not Connected"}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Kite User ID</Box>
                            <div>{userData.kite_user_id || "Not Set"}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Trading Status</Box>
                            <div>{userData.zerodhaConnected ? "Ready to Trade" : "Setup Required"}</div>
                          </div>
                        </SpaceBetween>
                      </ColumnLayout>
                      
                      <Box padding={{ top: "l" }}>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button>Edit Profile</Button>
                          {!userData.onboardingComplete && (
                            <Button variant="primary">Complete Onboarding</Button>
                          )}
                          {!userData.zerodhaConnected && (
                            <Button variant="primary">Connect Zerodha</Button>
                          )}
                        </SpaceBetween>
                      </Box>
                    </Container>
                  )
                },
                {
                  id: "market-overview",
                  label: "Market Overview",
                  content: (
                    <Container
                      header={
                        <Header variant="h2">Market Snapshot</Header>
                      }
                    >
                      <Box>
                        <p>Market data will be displayed here once connected to Zerodha.</p>
                        {!userData.zerodhaConnected && (
                          <Button>Connect to Zerodha</Button>
                        )}
                      </Box>
                    </Container>
                  )
                },
                {
                  id: "trading-strategies",
                  label: "Trading Strategies",
                  content: (
                    <Cards
                      cardDefinition={{
                        header: item => item.name,
                        sections: [
                          {
                            id: "description",
                            header: "Description",
                            content: item => item.description
                          },
                          {
                            id: "status",
                            header: "Status",
                            content: item => item.status
                          },
                          {
                            id: "performance",
                            header: "Performance",
                            content: item => item.performance
                          }
                        ]
                      }}
                      cardsPerRow={[
                        { cards: 1 },
                        { minWidth: 500, cards: 2 }
                      ]}
                      items={[
                        {
                          name: "Sample Strategy 1",
                          description: "This is a placeholder for your trading strategy.",
                          status: "Inactive",
                          performance: "N/A"
                        },
                        {
                          name: "Sample Strategy 2",
                          description: "Another placeholder for your trading strategy.",
                          status: "Inactive",
                          performance: "N/A"
                        }
                      ]}
                      loadingText="Loading strategies"
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>No strategies</b>
                          <Box
                            padding={{ bottom: "s" }}
                            variant="p"
                            color="inherit"
                          >
                            You don't have any trading strategies yet.
                          </Box>
                          <Button>Create strategy</Button>
                        </Box>
                      }
                      header={
                        <Header
                          actions={
                            <Button>Create strategy</Button>
                          }
                        >
                          Your Trading Strategies
                        </Header>
                      }
                    />
                  )
                }
              ]}
              ariaLabel="Trading information"
            />
          </ContentLayout>
        }
      />
    </>
  );
}